import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { PostgrestError } from '@supabase/supabase-js';

interface ABTestVariant {
  id: string;
  variant_key: string;
  name: string;
  overrides: {
    title?: string;
    description?: string;
    short_description?: string;
    cta_text?: string;
    cta_color?: string;
    image_url?: string;
  };
}

interface ABTestExperiment {
  id: string;
  name: string;
  target_id: string;
  variants: ABTestVariant[];
}

export const useABTest = (contentType: string, contentId: string) => {
  const [experiment, setExperiment] = useState<ABTestExperiment | null>(null);
  const [variant, setVariant] = useState<ABTestVariant | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessionId] = useState(() => {
    // Get or create session ID
    let sid = sessionStorage.getItem('ab_session_id');
    if (!sid) {
      sid = crypto.randomUUID();
      sessionStorage.setItem('ab_session_id', sid);
    }
    return sid;
  });

  useEffect(() => {
    const fetchExperiment = async () => {
      try {
        // Fetch active experiment for this content
        const { data: experimentData, error: expError } = await (supabase as any)
          .from('experiments')
          .select(`
            id,
            name,
            target_id,
            experiment_variants (
              id,
              variant_key,
              name,
              allocation_percentage,
              overrides,
              is_control
            )
          `)
          .eq('type', contentType)
          .eq('target_id', contentId)
          .eq('status', 'active')
          .maybeSingle();

        if (expError) throw expError;
        if (!experimentData) {
          setLoading(false);
          return;
        }

        const variants = experimentData.experiment_variants || [];
        if (variants.length === 0) {
          setLoading(false);
          return;
        }

        // Check if user already has a variant assigned (sticky assignment)
        const storageKey = `ab_variant_${experimentData.id}`;
        let assignedVariantId = sessionStorage.getItem(storageKey);
        
        let selectedVariant: ABTestVariant;
        
        if (assignedVariantId) {
          // Use previously assigned variant
          selectedVariant = variants.find((v: any) => v.id === assignedVariantId) || variants[0];
        } else {
          // Assign variant based on allocation percentages
          const rand = Math.random() * 100;
          let cumulative = 0;
          selectedVariant = variants[0];
          
          for (const v of variants) {
            cumulative += v.allocation_percentage;
            if (rand <= cumulative) {
              selectedVariant = v;
              break;
            }
          }
          
          // Store assignment
          sessionStorage.setItem(storageKey, selectedVariant.id);
        }

        setExperiment({
          id: experimentData.id,
          name: experimentData.name,
          target_id: experimentData.target_id,
          variants: variants
        });
        setVariant(selectedVariant);

        // Track impression
        await trackImpression(experimentData.id, selectedVariant.id);
        
      } catch (error) {
        console.error('Error fetching A/B test:', error);
      } finally {
        setLoading(false);
      }
    };

    if (contentId) {
      fetchExperiment();
    }
  }, [contentType, contentId]);

  const trackImpression = async (experimentId: string, variantId: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      await (supabase as any).from('experiment_impressions').insert({
        experiment_id: experimentId,
        variant_id: variantId,
        user_id: user?.id || null,
        anon_id: user ? null : sessionId,
        session_id: sessionId,
        page_url: window.location.href
      });

      // Increment counter
      await (supabase as any).rpc('increment_variant_impression', { p_variant_id: variantId });
    } catch (error) {
      console.error('Error tracking impression:', error);
    }
  };

  const trackConversion = async (conversionType: string, metadata?: any) => {
    if (!experiment || !variant) return;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      await (supabase as any).from('experiment_conversions').insert({
        experiment_id: experiment.id,
        variant_id: variant.id,
        user_id: user?.id || null,
        anon_id: user ? null : sessionId,
        session_id: sessionId,
        conversion_type: conversionType,
        metadata: metadata || {}
      });

      // Increment counter
      await (supabase as any).rpc('increment_variant_conversion', { p_variant_id: variant.id });
    } catch (error) {
      console.error('Error tracking conversion:', error);
    }
  };

  return {
    experiment,
    variant,
    loading,
    trackConversion,
    isTestActive: !!experiment
  };
};
